import { uniqueId } from "lodash";
import { RefObject, useEffect, useRef } from "react";
import "./BlockPointerEvents.less";

type Props = {
    /** Elements on which poiner events should work */
    activeRefs: RefObject<HTMLElement>[];
    /** No change in pointer event handling */
    disabled?: boolean;
};

// keep in sync with BlockPointerEvents.less
export const disable_pointer = "disable-pointer-id",
    enable_pointer = "enable-pointer-id";

/** Prevents pointer events anywhere except for specified elements and their scrollable parents */
export const useBlockPointerEvents = ({ activeRefs, disabled }: Props) => {
    const id = useRef<string>();
    useEffect(() => {
        id.current = uniqueId("p-");
    }, []);

    const isForced = (x: Element) => activeRefs.some(ref => x === ref.current);

    const enablePointer = (x: Element) => !x.hasAttribute(enable_pointer) && x.setAttribute(enable_pointer, id.current);

    const disablePointer = (elements: HTMLCollection) =>
        Array.from(elements).forEach(
            x =>
                isDiv(x) &&
                !isForced(x) &&
                !x.hasAttribute(enable_pointer) &&
                !x.hasAttribute(disable_pointer) &&
                x.id !== "ui-datepicker-div" &&
                x.setAttribute(disable_pointer, id.current)
        );

    const adjustPointers = (x: HTMLElement) => {
        if (!x) {
            return;
        }

        if (isForced(x) || isScrollable(x) || x === document.body) {
            enablePointer(x);
        }

        if (!isForced(x) && x.hasAttribute(enable_pointer)) {
            disablePointer(x.children);
        }

        adjustPointers(x.parentElement);
    };

    useEffect(() => {
        if (activeRefs.some(x => !x.current) || disabled) {
            return;
        }

        activeRefs.forEach(ref => adjustPointers(ref.current));

        return () => {
            document
                .querySelectorAll(`[${disable_pointer}="${id.current}"], [${enable_pointer}="${id.current}"]`)
                .forEach(x => (x.removeAttribute(disable_pointer), x.removeAttribute(enable_pointer)));
        };
    }, [...activeRefs.map(x => x.current), disabled]);
};

const isScrollable = (x: Element) => {
    if (!isDiv(x)) {
        return false;
    }

    const { overflow, overflowX, overflowY } = window.getComputedStyle(x);
    return /auto|scroll/.test(overflow + overflowY + overflowX);
};

const isDiv = (x: Element) => x.tagName === "DIV";

/**
 * When any popup opens, it disables pointer-events for all non-scrollable elements except its own trigger/content.
 *
 * If a child popup is open inside its parent popup, the child disables pointer-events for the parent.
 * So we can easily check if the parent content has pointer-events disabled.
 *
 * Note: need to check the children - not popup itself, because it always has 'enable_pointer' marker (see useBlockPointerEvents hook).
 */
export const isBlockedByChildPopup = (popup: HTMLElement) => popup.children[0].hasAttribute(disable_pointer);
